/**
 * theme.ts — Teams appearance-mode honorer.
 *
 * ROP-teams runs ONLY inside Teams (desktop + iOS), so the theme comes ONLY
 * from the Teams host (Bill 2026-11-03):
 *   1. app.getContext() → context.app.theme ("default" | "dark" | "contrast")
 *      for the first paint after the Teams handshake, and
 *   2. app.registerOnThemeChangeHandler(cb), so switching the theme in Teams
 *      (Settings → Appearance, or "Follow OS" flipping) re-colours ROP live
 *      with no reload.
 * A missing or unknown theme string means the Teams default (light) theme.
 * There is deliberately NO prefers-color-scheme / OS fallback: before 0.157.2
 * the OS media-query listeners stayed subscribed inside Teams, so an OS
 * light/dark flip could override the theme the user picked in Teams.
 *
 * We translate the Teams string to `<html data-theme="light|dark|contrast">`
 * (and mirror it on <body>). styles/tokens.css redefines every colour token
 * per data-theme, so components re-colour without code changes.
 */
import * as teamsJs from '@microsoft/teams-js';
import { initTeams, getTeamsContext } from './teams';

export type ThemeName = 'light' | 'dark' | 'contrast';

/** Map a Teams theme string to ours. Missing / unknown → Teams default (light). */
export function fromTeamsTheme(t: string | null | undefined): ThemeName {
  switch ((t ?? '').toLowerCase()) {
    case 'dark':
      return 'dark';
    case 'contrast':
    case 'highcontrast':
    case 'high-contrast':
      return 'contrast';
    default:
      return 'light';
  }
}

/** Write the theme to <html> and <body>. Idempotent. */
export function applyTheme(next: ThemeName): void {
  if (typeof document === 'undefined') return;
  if (document.documentElement.getAttribute('data-theme') !== next) {
    document.documentElement.setAttribute('data-theme', next);
  }
  if (document.body && document.body.getAttribute('data-theme') !== next) {
    document.body.setAttribute('data-theme', next);
  }
}

/** Read the theme string off a Teams context (current SDK: app.theme; very old hosts: theme). */
function themeFromContext(ctx: unknown): string | undefined {
  const c = ctx as { app?: { theme?: string }; theme?: string } | null | undefined;
  return c?.app?.theme ?? c?.theme;
}

export interface ThemeBridgeDeps {
  initTeams: () => Promise<boolean>;
  getContext: () => Promise<unknown>;
  registerOnThemeChangeHandler: (handler: (theme: string) => void) => void;
}

const defaultDeps: ThemeBridgeDeps = {
  initTeams,
  // initTeams() already fetched and cached the context; reuse it, and only
  // ask the SDK again if the cache is somehow empty.
  getContext: async () => getTeamsContext() ?? teamsJs.app.getContext(),
  registerOnThemeChangeHandler: (h) => teamsJs.app.registerOnThemeChangeHandler(h),
};

/**
 * Install the theme bridge. Called once from main.tsx before App mounts.
 * Paints the Teams default (light) immediately, then applies the Teams
 * context theme and subscribes to live theme changes.
 */
export async function installThemeBridge(deps: ThemeBridgeDeps = defaultDeps): Promise<void> {
  applyTheme('light');
  try {
    const inTeams = await deps.initTeams();
    if (!inTeams) return; // not hosted in Teams: App.tsx shows its gate page; keep the default.
    const ctx = await deps.getContext();
    applyTheme(fromTeamsTheme(themeFromContext(ctx)));
    deps.registerOnThemeChangeHandler((theme: string) => {
      applyTheme(fromTeamsTheme(theme));
    });
  } catch {
    // Teams theme API unavailable — stay on the Teams default (light).
  }
}

/** Diagnostic — read the currently applied theme. */
export function getAppliedTheme(): ThemeName {
  if (typeof document === 'undefined') return 'light';
  const v = document.documentElement.getAttribute('data-theme');
  return v === 'dark' || v === 'contrast' ? v : 'light';
}
